import { Children, isValidElement } from "react";
import type { ComponentType, JSX, ReactNode } from "react";
import type { Components } from "react-markdown";
import { cn } from "@volca/ui/lib/utils";
import { DROPPED, WORD } from "./diff-marks";
import type { DiffBlock } from "./rules-diff";
import { DROP_CLOSE, DROP_OPEN, MARK_CLOSE, MARK_OPEN } from "./rules-marks";

const MARK_BOUNDARY = new RegExp(
  `([${DROP_OPEN}${DROP_CLOSE}${MARK_OPEN}${MARK_CLOSE}])`,
);

/**
 * Marks the searched words themselves, wherever markdown put them.
 *
 * The first attempt tinted the whole passage a match fell in, and a passage
 * here is a section of the document, not a line: searching a common word
 * washed half the page blue and said nothing about where the word was. The
 * grid highlights the cell, and a word is this document's cell.
 *
 * Same two strengths as everywhere else: every hit takes the lighter blue, and
 * the hits in the passage the reader has stepped to take the stronger one.
 */
export const HIT = "bg-information-hovered";
export const HIT_ACTIVE = "bg-information-bold text-on-information";

const findIn = (
  nodes: ReactNode,
  needle: string,
  className: string,
): ReactNode =>
  Children.map(nodes, (node) => {
    if (typeof node === "string") {
      const lower = node.toLowerCase();
      if (!lower.includes(needle)) return node;
      const parts: ReactNode[] = [];
      let from = 0;
      for (
        let at = lower.indexOf(needle);
        at !== -1;
        at = lower.indexOf(needle, from)
      ) {
        if (at > from) parts.push(node.slice(from, at));
        parts.push(
          <mark key={at} className={cn("rounded-sm px-px", className)}>
            {node.slice(at, at + needle.length)}
          </mark>,
        );
        from = at + needle.length;
      }
      parts.push(node.slice(from));
      return <>{parts}</>;
    }
    if (isValidElement<{ children?: ReactNode }>(node) && node.props.children)
      return {
        ...node,
        props: {
          ...node.props,
          children: findIn(node.props.children, needle, className),
        },
      };
    return node;
  });

/** Every string in a subtree, so a mark can be looked for across its parts. */
const textOf = (nodes: ReactNode): string => {
  let text = "";
  Children.forEach(nodes, (node) => {
    if (typeof node === "string") text += node;
    else if (
      isValidElement<{ children?: ReactNode }>(node) &&
      node.props.children
    )
      text += textOf(node.props.children);
  });
  return text;
};

const marked = (nodes: ReactNode, className: string): ReactNode => {
  // Decided over the whole cell, before its parts are walked. A bold value
  // puts the old and the new marks inside two different `strong` elements, so
  // neither string node can see the other and each read as a lone change.
  const text = textOf(nodes);
  const both = text.includes(DROP_OPEN) && text.includes(MARK_OPEN);
  // A mark can open before a strong/link/em element and close after it. Carry
  // its state through the whole subtree, then reset it for the next cell.
  let active: "removed" | "added" | null = null;
  const markText = (value: string) =>
    value.split(MARK_BOUNDARY).map((part, index) => {
      if (part === DROP_OPEN || part === MARK_OPEN) {
        active = part === DROP_OPEN ? "removed" : "added";
        return both ? (
          <span key={index} className="sr-only">
            {active === "removed" ? "Previously" : "Now"}{" "}
          </span>
        ) : null;
      }
      if (part === DROP_CLOSE || part === MARK_CLOSE) {
        active = null;
        return null;
      }
      if (!part || !active) return part;
      return (
        <mark
          key={index}
          className={cn(
            "rounded-sm bg-transparent px-px",
            active === "removed" ? DROPPED : className,
          )}
        >
          {part}
        </mark>
      );
    });
  const walk = (children: ReactNode): ReactNode =>
    Children.map(children, (node) => {
      if (typeof node === "string") return markText(node);
      if (isValidElement<{ children?: ReactNode }>(node) && node.props.children)
        return {
          ...node,
          props: { ...node.props, children: walk(node.props.children) },
        };
      return node;
    });
  return walk(nodes);
};

// Every element that can hold the document's own text. The shared components
// map only overrides tables, so the marks have to be applied at each of these
// rather than by wrapping what that map happens to define.
const TEXT_TAGS = [
  "p",
  "li",
  "td",
  "th",
  "blockquote",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
] as const;

// A wide table still has to scroll in its own box rather than widen the pane.
const BASE_COMPONENTS: Components = {
  table: ({ node: _node, ...props }) => (
    <div className="overflow-x-auto">
      <table {...props} />
    </div>
  ),
};

export const componentsFor = (
  kind: DiffBlock["kind"],
  // Lowercased, and empty when nothing is being searched.
  needle: string,
  hit: string,
) => {
  const diffed = kind !== "same";
  if (!diffed && !needle) return BASE_COMPONENTS;
  const wrapped: Record<string, ComponentType<Record<string, unknown>>> = {};
  for (const tag of TEXT_TAGS) {
    const Tag = tag as keyof JSX.IntrinsicElements;
    wrapped[tag] = ({ node: _node, children, ...rest }) => {
      // Diff marks first, then the search over the result: the search has to
      // see the words as the reader does, including the ones the diff has
      // already wrapped.
      const body = diffed
        ? marked(children as ReactNode, WORD[kind])
        : (children as ReactNode);
      return <Tag {...rest}>{needle ? findIn(body, needle, hit) : body}</Tag>;
    };
  }
  return { ...BASE_COMPONENTS, ...wrapped };
};
